import { useState, useEffect, useRef } from 'react';
import { useConfirmDialog, ConfirmDialog } from './components/ConfirmDialog';
import { FORMULES, CATEGORIES } from './formSteps';
import { getNutritionConsultations, deleteClient, createCycleReview, getCycleReviews, forceSyncAllConsultations, saveClient, getSyncHealth } from './store';
import { getCurrentUser } from './supabaseClient';
import ClientStatusBadge from './ClientStatusBadge';
import ClientEngagementBadge from './ClientEngagementBadge';
import ClientNewFeedbacksBadge from './ClientNewFeedbacksBadge';
import JourneyNextActionBadge from './JourneyNextActionBadge';
import { isReturnClient, daysSinceLastConsultation } from './services/returnDiagnostic';
import {
  buildPackFollowupSchedule,
  getNextPendingStep,
  getPackCompletion,
  PACK_DEFINITIONS,
  canSendPackReview,
  isContinuousFollowupPack,
} from './services/packSystem';
import { getClientNutritionLocale } from './services/nutritionLocale';
import { markClientReviewed } from './services/markClientReviewed';
import { fetchClientAppConfig } from './services/clientAppConfig';
import { clearStatusCache } from './services/fetchClientsStatus';
import ClientesSilencieuses from './components/ClientesSilencieuses';
import { PARTNER_IDENTITY, emailSubjectQuestionnaire, emailSubjectFollowupReview } from './services/coachIdentity';
import AnalysisPlansFollowupBlock from './AnalysisPlansFollowupBlock';
// 2026-07-22 — « Le point de la semaine » (remplace le digest email du lundi).
import WeeklyPulse from './WeeklyPulse';
// V97.19 — Cockpit edition garde-fous cliniques (modal).
import ClinicalGuardrailsPanel from './components/ClinicalGuardrailsPanel';
import AlertIcon from './components/AlertIcon';
// V97.21 (OBS-2) — Dashboard stats observability.
import ObservabilityPanel from './components/ObservabilityPanel';
// V97.22 — Cockpit recommandations par phase.
import PhaseRecommendationsPanel from './components/PhaseRecommendationsPanel';
// V97.22.2 — Preload phase recommendations depuis DB pour readiness V97.18 D-F.
import { preloadPhaseRecommendationsFromSupabase } from './services/protocolPhases';
import { supabase } from './supabaseClient';
// V97.23.1 (V97.18 Phase F) — Cockpit brouillons IA en attente de validation.
import PendingDraftsPanel from './components/PendingDraftsPanel';
import { countAllPendingDrafts } from './services/planDraftsService';
import { deciderPourEvenement, journalPourDecision, nettoyerMessageErreur, DECISION } from './services/realtimeSessionPolicy';

// V86.2 : prend le client entier pour pouvoir brancher FR/EN via getClientNutritionLocale.
// Cliente FR (defaut) → pre-questionnaire /questionnaire/:id (inchange).
// Cliente EN Benfitcoach (suivi/intensif + langue EN) → anamnese complete EN /anamnese/:id,
// pas de pre-questionnaire.
function SendQuestionnaireButton({ client }) {
  const locale = getClientNutritionLocale(client);
  const clientId = client.id;
  const clientEmail = client.form?.email;
  const clientPrenom = client.prenom || client.form?.prenom || '';
  const handleSend = (e) => {
    e.stopPropagation();
    let url, subject, body;
    if (locale === 'EN') {
      url = `${window.location.origin}/anamnese/${clientId}`;
      subject = emailSubjectQuestionnaire('en');
      body =
        `Hello ${clientPrenom},\n\n` +
        `Ahead of our consultation, please take 10-15 minutes to complete your personalized health assessment:\n\n` +
        `➜ ${url}\n\n` +
        `All information is strictly confidential and used only to prepare your nutrition plan.`;
    } else {
      url = `${window.location.origin}/questionnaire/${clientId}`;
      subject = emailSubjectQuestionnaire('fr');
      body =
        `Bonjour ${clientPrenom},\n\n` +
        `Avant notre consultation, merci de remplir ce court questionnaire (5 minutes) :\n\n` +
        `➜ ${url}\n\n` +
        `Ce questionnaire est strictement confidentiel.`;
    }
    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(clientEmail || '')}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };
  return (
    <button onClick={handleSend} style={{
      display: 'block', width: '100%', textAlign: 'left',
      padding: '10px 16px', background: 'none', border: 'none',
      color: 'var(--text)', cursor: 'pointer', fontSize: '.85rem',
    }}>
      {locale === 'EN' ? 'Send health assessment' : 'Envoyer questionnaire'}
    </button>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getInitial(name) {
  return (name || '?').charAt(0).toUpperCase();
}

function getFollowUpStatus(clientId) {
  const consultations = getNutritionConsultations(clientId);
  if (consultations.length === 0) return null;
  const lastDate = new Date(consultations[0].date);
  const now = new Date();
  const diffMs = now - lastDate;
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30.44);
  if (diffMonths >= 6) return 'urgent';
  if (diffMonths >= 3) return 'recommended';
  return null;
}

function ClientCard({ client, i, onConsultation, onEditConsultation, onViewHistory, onOpen, isOwn, onRefresh, onSendPackReview }) {
  const confirmDialog = useConfirmDialog();
  const [menuOpen, setMenuOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('loading');
  const [transferExpanded, setTransferExpanded] = useState(false);
  const consultations = getNutritionConsultations(client.id);
  const followUp = getFollowUpStatus(client.id);
  const lastConsultation = consultations[0];
  const isReturn = isReturnClient(client);
  const daysSince = isReturn ? daysSinceLastConsultation(client) : null;
  const [sending, setSending] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const packDef = client.packType ? PACK_DEFINITIONS[client.packType] : null;
  const isFollowupPack = isContinuousFollowupPack(client.packType);
  const nextStep = isFollowupPack ? getNextPendingStep(client) : null;
  const completion = isFollowupPack ? getPackCompletion(client) : null;
  const showSendBtn = canSendPackReview(nextStep);
  const packSteps = isFollowupPack ? buildPackFollowupSchedule(client) : [];

  // La periode existe uniquement apres publication reussie du programme.
  const isDelivered = isFollowupPack && client.packStartedAtConfirmed === true && Boolean(client.packStartedAt);
  const totalPackDays = packDef?.durationWeeks ? packDef.durationWeeks * 7 : null;
  const daysSinceDelivery = isDelivered
    ? Math.max(0, Math.floor((Date.now() - new Date(client.packStartedAt).getTime()) / 86400000))
    : null;
  // Position en % de la bulle "today" sur la timeline (0-100)
  const todayProgressPct = isDelivered && totalPackDays
    ? Math.min(100, Math.max(0, (daysSinceDelivery / totalPackDays) * 100))
    : 0;

  useEffect(() => {
    getCycleReviews(client.id).then(reviews => {
      if (!reviews.length) { setReviewStatus('not_sent'); return; }
      setReviewStatus(reviews[0].status);
    });
  }, [client.id]);
  const objectif = client.form?.objectif
    || (client.form?.symptomesObjectifs || []).slice(0, 2).join(', ')
    || client.form?.objectifPrincipal
    || null;

  const handleDelete = async (e) => {
    e.stopPropagation();
    setMenuOpen(false);

    // 2026-07-31 (décision Benoit) — l'app cliente vit dans une AUTRE base :
    // supprimer la fiche ici ne touche pas le compte de la cliente là-bas.
    // On ne cascade PAS automatiquement (une fausse manœuvre couperait
    // l'accès d'une vraie cliente, et la corbeille d'ici ne restaurerait
    // rien là-bas) : on le DIT au moment de décider, Anissa garde la main.
    // Vérification best-effort : si l'app est injoignable on ne bloque
    // jamais la suppression, mais on ne prétend pas non plus qu'il n'y a
    // pas de compte.
    let appNotice = '';
    try {
      const { found } = await fetchClientAppConfig(client);
      if (found) {
        appNotice =
          "\n\nElle a un compte dans l'app cliente : son accès et ses données y resteront actifs. "
          + "Pour les retirer aussi, la suppression se fait depuis l'app (Paramètres → Tes données).";
      }
    } catch {
      appNotice =
        "\n\nImpossible de vérifier si elle a un compte dans l'app cliente (app injoignable) — à contrôler séparément.";
    }

    const ok = await confirmDialog.ask({
      title: 'Supprimer ce client',
      // 2026-07-22 : message renforcé pour les clientes venant du coaching
      // Benoit (fiche + historique nutrition disparaissent de cet espace).
      message: (isOwn
        ? 'Cette action est irréversible. Toutes les données associées seront perdues.'
        : 'Cliente venue du coaching Benoit : sa fiche et son historique nutrition disparaîtront de cet espace. Cette action est irréversible.')
        + appNotice,
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    deleteClient(client.id);
    onRefresh();
  };

  const handleSendReview = async (e) => {
    e.stopPropagation();
    const user = await getCurrentUser();
    const token = await createCycleReview(client.id, null, user?.id);
    if (!token) { alert('Erreur lors de la création du bilan'); return; }
    const url = `${window.location.origin}/review/${token}`;
    const prenom = client.prenom || '';
    const subject = emailSubjectFollowupReview('fr');
    const body =
      `Bonjour ${prenom},\n\n` +
      `4 semaines se sont écoulées depuis ton plan nutrition.\n` +
      `Merci de remplir ce bilan rapide (2 minutes) :\n\n` +
      `➜ ${url}\n\n` +
      `Cela m'aidera à ajuster ton plan pour la suite.`;
    const gmailUrl = `https://mail.google.com/mail/?view=cm` +
      `&to=${encodeURIComponent(client.form?.email || '')}` +
      `&su=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
    setReviewStatus('sent');
  };

  const urgencyColor = followUp === 'urgent'
    ? '#e05252' : followUp === 'recommended'
    ? '#e09a3a' : 'transparent';

  return (
    <div
      className="anissa-client-card-mobile"
      style={{
        background: 'rgba(255,255,255,.04)',
        border: '1px solid rgba(255,255,255,.08)',
        borderLeft: `3px solid ${urgencyColor === 'transparent' ? 'rgba(106,191,138,.3)' : urgencyColor}`,
        borderRadius: 14,
        padding: '16px 20px',
        minHeight: 'auto',
        cursor: isOwn && onOpen ? 'pointer' : 'default',
        transition: 'all .2s',
        position: 'relative',
        animationDelay: `${i * 50}ms`,
      }}
      onClick={isOwn && onOpen ? () => onOpen(client.id) : undefined}
    >
      {/* Zone 1 — Avatar */}
      <div className="anissa-client-card__avatar" style={{
        width: 48, height: 48, borderRadius: '50%',
        background: 'rgba(106,191,138,.15)',
        border: '1.5px solid rgba(106,191,138,.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1rem', fontWeight: 700, color: '#8abf9a', flexShrink: 0,
      }}>
        {getInitial(client.prenom)}
      </div>

      {/* Zone 2 — Infos */}
      <div className="anissa-client-card__content" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div className="anissa-client-card__heading" style={{
          display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
          flexWrap: 'wrap', rowGap: 4,
        }}>
          <span className="anissa-client-card__name" style={{
            fontWeight: 700, fontSize: '1.05rem', color: '#f0f0e8',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {client.prenom || 'Sans nom'}
          </span>
          <span className="anissa-client-card__badge-slot anissa-client-card__badge-slot--status">
            <ClientStatusBadge email={client.form?.email} stagingClientId={client.stagingClientId} />
          </span>
          <span className="anissa-client-card__badge-slot anissa-client-card__badge-slot--engagement">
            <ClientEngagementBadge email={client.form?.email} stagingClientId={client.stagingClientId} />
          </span>
          <span className="anissa-client-card__badge-slot anissa-client-card__badge-slot--feedbacks">
            <ClientNewFeedbacksBadge email={client.form?.email} stagingClientId={client.stagingClientId} />
          </span>
          {/* V97.36 (2026-06-16) : 4e pastille "prochaine action" (Journey
              Resolver V1). Lecture seule, dérivée des faits déjà chargés
              (form + journey_state + clients-status). Ne remplace aucun badge. */}
          <span className="anissa-client-card__badge-slot anissa-client-card__badge-slot--next-action">
            <JourneyNextActionBadge client={client} email={client.form?.email} stagingClientId={client.stagingClientId} />
          </span>
          {/* V97.8.1 (2026-05-12) : badge "Pré-q reçu" pour signaler à Anissa
              qu'une cliente a soumis son pré-questionnaire. Disparaît une
              fois l'anamnèse validée par Anissa au RDV. Inline pour rester
              simple — pas besoin d'un fetch async (les champs sont dans
              client.form déjà chargé). */}
          {(() => {
            const f = client.form || {};
            const preQReceived = !!(f.objectif_primaire || f.dureeProbleme || f.ressentiDigestion);
            const anamnesisValidated = client.journey_state?.anamnesis_validated === true;
            // 2026-07-19 (revue Benoit) — le badge s'éteint aussi dès qu'une
            // consultation existe : le RDV a eu lieu, le pré-questionnaire a
            // forcément été lu. Avant, il restait allumé des mois (Camille,
            // rempli en mai) si anamnesis_validated n'était jamais coché.
            if (!preQReceived || anamnesisValidated || consultations.length > 0) return null;
            return (
              <span
                className="anissa-client-card__badge anissa-client-card__badge--questionnaire"
                title="La cliente a rempli son pré-questionnaire — à lire avant le RDV"
                style={{
                  fontSize: '.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                  background: 'rgba(106,191,138,.18)', color: '#8abf9a',
                  border: '1px solid rgba(106,191,138,.35)',
                  display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                }}
              >
                Pré-q reçu
              </span>
            );
          })()}
        </div>
        <div className="anissa-client-card__meta" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
          {consultations.length > 0
            ? `${consultations.length} consultation${consultations.length > 1 ? 's' : ''} \u00b7 derni\u00e8re ${formatDate(lastConsultation?.date)}`
            : 'Aucune consultation'}
        </div>
        {followUp && (
          <span className={`anissa-client-card__badge anissa-client-card__badge--followup anissa-client-card__badge--followup-${followUp}`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: '.72rem', fontWeight: 600, padding: '3px 10px',
            borderRadius: 20, alignSelf: 'flex-start',
            background: followUp === 'urgent' ? 'rgba(224,82,82,.15)' : 'rgba(224,154,58,.15)',
            color: followUp === 'urgent' ? '#e05252' : '#e09a3a',
            border: `1px solid ${followUp === 'urgent' ? 'rgba(224,82,82,.3)' : 'rgba(224,154,58,.3)'}`,
          }}>
            {followUp === 'urgent' ? '\u26a0 Suivi urgent' : '\u25cb Suivi recommand\u00e9'}
          </span>
        )}
        {reviewStatus === 'submitted' && (
          <span
            className="anissa-client-card__badge anissa-client-card__badge--review"
            onClick={e => e.stopPropagation()}
            style={{
              display:'inline-flex', alignItems:'center', gap:4,
              fontSize:'.7rem', fontWeight:600, padding:'2px 8px',
              borderRadius:20, alignSelf:'flex-start',
              background:'rgba(74,222,128,.1)',
              color:'#4ade80',
              border:'1px solid rgba(74,222,128,.2)',
              marginBottom: 4,
              cursor: 'default',
            }}>
            ✓ Bilan reçu
          </span>
        )}
        {isReturn && (
          <span className="anissa-client-card__badge anissa-client-card__badge--return" style={{
            display:'inline-flex', alignItems:'center', gap:4,
            fontSize:'.7rem', fontWeight:600, padding:'2px 8px',
            borderRadius:20, alignSelf:'flex-start',
            background:'rgba(96,165,250,.1)',
            color:'#60a5fa',
            border:'1px solid rgba(96,165,250,.2)',
            marginBottom:4,
          }}>
            Reprise — {daysSince}j
          </span>
        )}
        {client.form?.referredBy === 'benoit' && (
          <>
            <span
              className="anissa-client-card__badge anissa-client-card__badge--transfer"
              onClick={e => { e.stopPropagation(); if (client.form?.anissaTransferSummary) setTransferExpanded(v => !v); }}
              title={client.form?.anissaTransferSummary
                ? `Transmission ${PARTNER_IDENTITY.name} disponible — cliquer pour ${transferExpanded ? 'masquer' : 'afficher'} le résumé`
                : `Client de ${PARTNER_IDENTITY.name}`}
              style={{
                display:'inline-flex', alignItems:'center', gap:4,
                fontSize:'.7rem', fontWeight:600, padding:'2px 8px',
                borderRadius:20, alignSelf:'flex-start',
                background:'rgba(196,160,80,.12)',
                color:'#c4a050',
                border:'1px solid rgba(196,160,80,.3)',
                marginBottom:4,
                cursor: client.form?.anissaTransferSummary ? 'pointer' : 'default',
              }}
            >
              Client de {PARTNER_IDENTITY.name}
              {client.form?.anissaTransferStatus && client.form.anissaTransferStatus !== 'sent' ? ` · ${client.form.anissaTransferStatus}` : ''}
              {client.form?.anissaTransferSummary ? (transferExpanded ? ' ▴' : ' ▾') : ''}
            </span>
            {client.form?.anissaTransferSummary && !transferExpanded && (
              <span className="anissa-client-card__transfer-hint" style={{
                fontSize:'.68rem', color:'rgba(196,160,80,.75)',
                fontStyle:'italic', marginTop:-2, marginBottom:4,
              }}>
                Résumé transmission disponible (cliquer badge)
              </span>
            )}
            {client.form?.anissaTransferSummary && transferExpanded && (
              <div
                className="anissa-client-card__transfer-summary"
                onClick={e => e.stopPropagation()}
                style={{
                  marginTop: 2, marginBottom: 6,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'rgba(196,160,80,0.06)',
                  border: '1px solid rgba(196,160,80,0.25)',
                  whiteSpace: 'pre-wrap',
                  fontSize: '.72rem',
                  lineHeight: 1.5,
                  color: 'var(--text)',
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {client.form.anissaTransferSummary}
                {client.form?.anissaTransferAt && (
                  <div className="anissa-client-card__transfer-date" style={{ marginTop: 6, fontSize: '.9em', color: 'var(--text-muted)' }}>
                    Transmis le {formatDate(client.form.anissaTransferAt)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {isFollowupPack && packDef && (
          <div className="anissa-client-card__timeline" style={{
            margin: '10px 0 4px',
            padding: '10px 12px',
            background: 'rgba(255,255,255,.03)',
            border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 8,
          }}>
            {/* Header : label + compteur */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}>
              <span style={{ color: '#d9d7c6', fontWeight: 600, fontSize: '.75rem' }}>
                {packDef.label}
              </span>
              {completion && (
                <span style={{ color: 'rgba(255,255,255,.3)', fontSize: '.68rem' }}>
                  {completion.done}/{completion.total} étapes
                </span>
              )}
            </div>

            {/* Progress bar */}
            {completion && completion.total > 0 && (
              <div style={{
                width: '100%',
                height: 2,
                background: 'rgba(255,255,255,.06)',
                borderRadius: 2,
                marginBottom: 10,
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${completion.percent}%`,
                  background: completion.percent === 100
                    ? '#22c55e'
                    : 'linear-gradient(90deg, #22c55e, #6abf8a)',
                  borderRadius: 2,
                  transition: 'width .4s ease',
                }} />
              </div>
            )}

            {/* AX.1 (2026-05-11) — Encart 'Programme à remettre' + bouton
                'Marquer comme remis' SUPPRIMÉ. L'étape 7 Livraison du parcours
                gère ça avec plus de contexte (papier vs app, checklist, date).
                Règle produit : dashboard = actions macro / parcours = opérations. */}

            {/* V96.28 — Timeline temporelle (jalons positionnes au % temps reel) */}
            {isDelivered && packSteps.length > 0 && totalPackDays && (() => {
              const trackHeight = 6;
              const labelLineH = 18;
              const dotSize = 11;
              const todaySize = 16;
              return (
                <div style={{ marginBottom: 10 }}>
                  {/* En-tete : chip remis + jour J */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 12, gap: 12, flexWrap: 'wrap',
                  }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '5px 11px', borderRadius: 999,
                      background: 'rgba(95,189,130,.12)',
                      border: '1px solid rgba(95,189,130,.28)',
                      fontSize: '.7rem', fontWeight: 600, color: '#7fcd9b',
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: '#5fbd82', boxShadow: '0 0 5px #5fbd82',
                      }} />
                      Programme actif depuis le {new Date(client.packStartedAt).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                    <div style={{
                      fontSize: '.7rem', color: 'rgba(255,255,255,.45)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      Jour <span style={{ color: '#e0cd96', fontWeight: 700 }}>{daysSinceDelivery}</span>
                      <span style={{ color: 'rgba(255,255,255,.25)' }}> / {totalPackDays}</span>
                      <span style={{ color: 'rgba(255,255,255,.3)', marginLeft: 6 }}>
                        ({Math.round(todayProgressPct)}%)
                      </span>
                    </div>
                  </div>

                  {/* Track + jalons + bulle today, positionnement absolu au % temporel reel */}
                  <div style={{
                    position: 'relative',
                    height: trackHeight + dotSize + labelLineH + 8,
                    margin: '0 8px',  // marge pour eviter clipping bulle aux extremites
                  }}>
                    {/* Track de fond */}
                    <div style={{
                      position: 'absolute', top: dotSize / 2 + (todaySize - dotSize) / 2, left: 0, right: 0,
                      height: trackHeight, borderRadius: trackHeight / 2,
                      background: 'rgba(255,255,255,.05)',
                      border: '1px solid rgba(255,255,255,.04)',
                    }} />
                    {/* Track rempli (progression doree jusqu'a today) */}
                    <div style={{
                      position: 'absolute', top: dotSize / 2 + (todaySize - dotSize) / 2, left: 0,
                      width: `${todayProgressPct}%`,
                      height: trackHeight, borderRadius: trackHeight / 2,
                      background: 'linear-gradient(90deg, rgba(197,176,122,.45) 0%, rgba(224,205,150,.7) 100%)',
                      boxShadow: '0 0 8px rgba(197,176,122,.25)',
                      transition: 'width .4s ease',
                    }} />

                    {/* Jalons positionnes au % temporel reel */}
                    {packSteps.map((step) => {
                      const pct = Math.min(100, Math.max(0, (step.weekOffset / packDef.durationWeeks) * 100));
                      const isActive = nextStep?.stepNumber === step.stepNumber;
                      const isDone = step.status === 'done';
                      const isLate = step.isLate;
                      const isPast = (step.weekOffset * 7) <= daysSinceDelivery;
                      const dotColor = isDone ? '#5fbd82'
                        : isLate ? '#e57c6c'
                        : isPast ? '#e0cd96'
                        : 'rgba(255,255,255,.18)';
                      const ringColor = isActive ? 'rgba(224,205,150,.45)'
                        : isDone ? 'rgba(95,189,130,.3)'
                        : isLate ? 'rgba(229,124,108,.3)'
                        : 'rgba(255,255,255,.08)';
                      return (
                        <div
                          key={step.stepNumber}
                          onMouseEnter={() => setHoveredStep(step.stepNumber)}
                          onMouseLeave={() => setHoveredStep(null)}
                          style={{
                            position: 'absolute',
                            left: `calc(${pct}% - ${dotSize / 2}px)`,
                            top: (todaySize - dotSize) / 2,
                            width: dotSize, height: dotSize, borderRadius: '50%',
                            background: dotColor, border: `2px solid ${ringColor}`,
                            boxSizing: 'border-box',
                            cursor: 'help',
                            transition: 'all .2s',
                            zIndex: 2,
                          }}
                        >
                          {/* Label sous le jalon (S4, S8...) */}
                          <div style={{
                            position: 'absolute',
                            top: dotSize + 8, left: '50%', transform: 'translateX(-50%)',
                            fontSize: '.62rem', fontWeight: isActive ? 700 : 500,
                            color: isDone ? 'rgba(95,189,130,.85)'
                              : isLate ? '#e57c6c'
                              : isActive ? '#e0cd96'
                              : 'rgba(255,255,255,.35)',
                            letterSpacing: '.02em',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            S{step.weekOffset}
                          </div>
                          {/* Tooltip sur hover */}
                          {hoveredStep === step.stepNumber && (
                            <div style={{
                              position: 'absolute',
                              bottom: dotSize + 10, left: '50%', transform: 'translateX(-50%)',
                              minWidth: 170, padding: '8px 11px', borderRadius: 8,
                              background: 'rgba(14,21,15,.97)',
                              border: '1px solid rgba(197,176,122,.25)',
                              boxShadow: '0 4px 12px rgba(0,0,0,.4)',
                              zIndex: 30, pointerEvents: 'none',
                            }}>
                              <div style={{ fontSize: '.72rem', fontWeight: 600, color: '#e0cd96', marginBottom: 4 }}>
                                {step.label}
                              </div>
                              <div style={{
                                fontSize: '.66rem',
                                color: isDone ? '#5fbd82'
                                  : isLate ? '#e57c6c'
                                  : step.status === 'sent' ? '#e0cd96'
                                  : 'rgba(255,255,255,.5)',
                              }}>
                                {isDone ? '\u2713 Complete'
                                  : step.status === 'sent' ? '\u23f3 Envoye'
                                  : isLate ? '\u26a0 En retard'
                                  : step.isDueSoon ? '\u2192 A envoyer cette semaine'
                                  : '\u25cb A venir'}
                              </div>
                              {step.dueDate && (
                                <div style={{ fontSize: '.62rem', color: 'rgba(255,255,255,.35)', marginTop: 3 }}>
                                  {new Date(step.dueDate).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Bulle "aujourd'hui" — design clinique soigne */}
                    <div
                      title={`Aujourd'hui — Jour ${daysSinceDelivery} sur ${totalPackDays}`}
                      style={{
                        position: 'absolute',
                        left: `calc(${todayProgressPct}% - ${todaySize / 2}px)`,
                        top: 0,
                        width: todaySize, height: todaySize, borderRadius: '50%',
                        background: '#e0cd96',
                        border: '3px solid rgba(14,21,15,1)',
                        boxShadow: '0 0 0 2px rgba(197,176,122,.35), 0 0 12px rgba(197,176,122,.5)',
                        zIndex: 4,
                        transition: 'left .4s ease',
                      }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Prochaine étape texte */}
            {nextStep && (
              <div style={{
                fontSize: '.7rem',
                color: nextStep.isLate ? '#f87171'
                     : nextStep.isDueSoon ? '#fbbf24'
                     : 'rgba(255,255,255,.5)',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 2,
              }}>
                <span>→</span>
                {nextStep.type === 'review' && !nextStep.isLate
                  ? `${nextStep.label} à envoyer`
                  : nextStep.isLate
                  ? `${nextStep.label} — en retard`
                  : nextStep.label}
              </div>
            )}
          </div>
        )}
        {objectif && (
          <div className="anissa-client-card__objective" style={{ fontSize: '.8rem', color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {objectif}
          </div>
        )}
      </div>

      {/* Zone 3 — Actions */}
      <div className="anissa-client-card__actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        {/* V94.23 : bouton CTA contextuel selon next pack step.
            - Si etape proche (< 7j) ou en retard : bouton specifique 'Bilan S4'
              avec couleur d urgence (rouge si retard, ambre si du, vert si proche)
            - Sinon : '+ Nouvelle consultation' standard */}
        {(() => {
          const next = isFollowupPack ? nextStep : null;
          const dueDate = next ? new Date(next.dueDate) : null;
          const daysUntilDue = dueDate ? Math.floor((dueDate.getTime() - Date.now()) / 86400000) : null;
          const isLate = next?.isLate;
          const isImminent = !isLate && daysUntilDue !== null && daysUntilDue <= 7;

          if (next && (isLate || isImminent)) {
            // Bouton contextuel pour l etape proche/en retard
            const shortLabel = next.label.replace(/^Bilan\s+/, '').replace(/\s+—.*$/, '');
            const urgencyText = isLate
              ? `EN RETARD ${Math.abs(daysUntilDue)}j`
              : daysUntilDue === 0
                ? 'aujourd\u2019hui'
                : `dans ${daysUntilDue}j`;
            const bgColor = isLate
              ? 'rgba(212,92,76,.15)'
              : daysUntilDue <= 3
                ? 'rgba(232,160,64,.15)'
                : 'rgba(106,191,138,.15)';
            const borderColor = isLate
              ? 'rgba(212,92,76,.5)'
              : daysUntilDue <= 3
                ? 'rgba(232,160,64,.5)'
                : 'rgba(106,191,138,.5)';
            const textColor = isLate ? '#e09c8e' : daysUntilDue <= 3 ? '#e9b876' : '#8abf9a';

            return (
              <button
                className={`btn btn-sm anissa-client-card__contextual-cta${isLate ? ' anissa-client-card__contextual-cta--late' : daysUntilDue <= 3 ? ' anissa-client-card__contextual-cta--due' : ' anissa-client-card__contextual-cta--soon'}`}
                style={{
                  width: 'auto', padding: '8px 16px', whiteSpace: 'nowrap',
                  background: bgColor,
                  border: `1px solid ${borderColor}`,
                  color: textColor,
                  fontWeight: 600, fontSize: '.78rem',
                  borderRadius: 8,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
                onClick={(e) => { e.stopPropagation(); onConsultation(client.id); }}
                title={`Cree la consultation ${next.label}`}
              >
                <span>{isLate ? '\u23f0' : '\ud83d\udcc5'}</span>
                <span>+ {shortLabel}</span>
                <span className="anissa-client-card__contextual-deadline" style={{
                  fontSize: '.7rem', opacity: .85, fontWeight: 500,
                  background: 'rgba(0,0,0,.2)', padding: '2px 8px', borderRadius: 999,
                }}>{urgencyText}</span>
              </button>
            );
          }

          // AX.4 (2026-05-11) — Bouton renommé selon le statut du parcours.
          // 'Nouvelle consultation' était ambigu maintenant que tout passe par
          // le parcours. Le label reflète l'intention : démarrer vs continuer.
          const hasConsultations = consultations.length > 0;
          const journeyState = client?.journey_state || null;
          const isFollowingUp = !!journeyState?.followup_started;
          let label;
          if (isFollowingUp) {
            label = 'Continuer le suivi';
          } else if (hasConsultations) {
            label = 'Continuer le parcours';
          } else {
            label = 'Commencer le parcours';
          }
          return (
            <button
              className="btn btn-sm btn-anissa-primary"
              style={{ width: 'auto', padding: '8px 18px', whiteSpace: 'nowrap' }}
              onClick={(e) => { e.stopPropagation(); onConsultation(client.id); }}
              title="Ouvrir le parcours guidé de cette cliente"
            >
              {label} →
            </button>
          );
        })()}
        {/* AZ.4 + AZ.5 (2026-05-11) — Menu 'Plus' supprimé. Toutes les actions
            ont été migrées dans le parcours cliente :
            - 📋 Voir historique         → étape 8 cockpit (bouton 'Historique complet')
            - 📩 Envoyer questionnaire    → étape 1 Anamnèse
            - 📅 Modifier date remise     → étape 7 Livraison
            - ✏️ Modifier anamnèse        → bouton 'Profil' du header parcours
            - Tous les items bilan/plan reprise → étape 8 cockpit suivi
            Reste uniquement le bouton 'Supprimer' standalone (action destructive
            hors flow parcours, doit rester accessible depuis le dashboard). */}
        {/* 2026-07-22 (espace 100 % Anissa) : la corbeille est disponible sur
            TOUTES les clientes, y compris celles venues du coaching Benoit —
            le verrou isOwn protégeait les données de Benoit quand il utilisait
            encore ce SaaS ; il ne l'utilise plus, et ses clients tests doivent
            pouvoir être supprimés. Le dialogue de confirmation reste le
            garde-fou (message renforcé pour les transférées). */}
        {(
          <button
            type="button"
            onClick={handleDelete}
            title="Supprimer cette cliente"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,.10)',
              borderRadius: 8,
              cursor: 'pointer',
              color: 'rgba(255,255,255,.45)',
              fontSize: '.95rem',
              transition: 'all .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(224,82,82,.10)';
              e.currentTarget.style.borderColor = 'rgba(224,82,82,.35)';
              e.currentTarget.style.color = '#e05252';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,.10)';
              e.currentTarget.style.color = 'rgba(255,255,255,.45)';
            }}
          >
            <AlertIcon type="trash" size={16} />
          </button>
        )}
      </div>
      <ConfirmDialog state={confirmDialog.state} onClose={confirmDialog.close} />
    </div>
  );
}

export default function AnissaDashboard({ sharedClients, ownClients, onConsultation, onEditConsultation, onViewHistory, onNewClient, onOpenClient, onRefresh, onSendPackReview, onOpenSuivi }) {
  const [search, setSearch] = useState('');
  // V94.16 : filtre rapide par statut client (all / active / hors_app / recontact)
  const [statusFilter, setStatusFilter] = useState('all');
  // V97.11 : tri ('alert' = priorite urgence, 'recent' = derniere conso, 'name' = alphabetique)
  const [sortBy, setSortBy] = useState('alert');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // V97.11 — bouton "Tout marquer lu" (batch)
  const [markingAll, setMarkingAll] = useState(false);
  // 2026-07-19 (revue Benoit) — outils experts repliés hors du quotidien
  const [showTools, setShowTools] = useState(false);
  const [markAllResult, setMarkAllResult] = useState(null);
  // V97.19 — open/close cockpit garde-fous cliniques
  const [showGuardrailsPanel, setShowGuardrailsPanel] = useState(false);
  // V97.21 (OBS-2) — open/close dashboard stats observability
  const [showObservabilityPanel, setShowObservabilityPanel] = useState(false);
  // V97.22 — open/close cockpit phase recommendations
  const [showPhasesPanel, setShowPhasesPanel] = useState(false);
  // V97.23.1 (V97.18 Phase F) — Cockpit brouillons IA + badge count
  const [showDraftsPanel, setShowDraftsPanel] = useState(false);
  const [pendingDraftsCount, setPendingDraftsCount] = useState(0);

  // V97.23.1 — Compte les drafts pending au mount et refresh apres fermeture modal
  useEffect(() => {
    let alive = true;
    countAllPendingDrafts().then((c) => {
      if (alive) setPendingDraftsCount(c);
    });
    return () => { alive = false; };
  }, [showDraftsPanel]); // refresh apres close

  // V97.23.5 — Realtime subscription : badge live sans refresh.
  // Quand une transition de phase auto-genere un draft, le badge s'incremente
  // automatiquement chez Anissa. Quand elle Accept/Refuse, decremente aussi.
  //
  // V97.149 (2026-07-31) — deux correctifs apres constat sur la console
  // d'Anissa (websocket refuse cote navigateur) :
  //
  //  1. la publication `supabase_realtime` etait VIDE, donc aucun evenement
  //     ne partait meme websocket connecte (migration V97.149) ;
  //  2. le badge ne doit JAMAIS dependre du seul websocket. On recompte au
  //     retour sur l'onglet, au focus et a la reconnexion reseau. Si le
  //     temps reel tombe, le badge reste juste au premier coup d'oeil.
  //
  // L'etat du canal est journalise : c'est ce qui permet de verifier
  // SUBSCRIBED sans instrumenter davantage.
  useEffect(() => {
    let annule = false;
    const recompter = (raison) => {
      countAllPendingDrafts().then((c) => {
        if (!annule) setPendingDraftsCount(c);
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn(`[drafts-badge] recomptage echoue (${raison}) :`, e?.message || e);
      });
    };

    // V97.151 (2026-08-01) — BROADCAST sur canal PRIVE, en remplacement de
    // Postgres Changes. Motif : l'evenement DELETE de Postgres Changes n'est
    // pas filtre par la RLS, un contexte anonyme voyait passer les
    // suppressions (preuve : docs/RLS-PLAN-DRAFTS-PREUVES-2026-07-31.md).
    // Decision d'Anissa : ne pas conserver cette fuite, meme limitee a un
    // UUID technique.
    //
    // Le message recu ne contient AUCUNE donnee metier — c'est un simple
    // signal « les brouillons ont change ». Le comptage se refait ici, par
    // une requete deja protegee par la RLS (V97.150). Rien de sensible ne
    // transite donc par le canal.
    //
    // `config.private: true` est indispensable : sans lui le canal serait
    // public et l'autorisation posee sur realtime.messages ne s'appliquerait
    // pas.
    // Le JWT de la session DOIT etre pousse au client Realtime avant de
    // rejoindre un canal prive : la cle anonyme ne suffit pas, le serveur
    // repond « HTTP Authentication failed; no valid credentials available ».
    //
    // Deux pieges cumules, constates en production le 2026-08-01 :
    //
    //  1. supabase-js ne pousse le jeton automatiquement que sur SIGNED_IN et
    //     TOKEN_REFRESHED. Au RECHARGEMENT d'une page avec session restauree,
    //     l'evenement est INITIAL_SESSION : le jeton n'est jamais pousse.
    //
    //  2. `setAuth()` SANS ARGUMENT ne va pas chercher la session. Le client
    //     est cree par createClient(url, anonKey), donc sans callback
    //     `accessToken` : _performAuth(null) retombe sur accessTokenValue,
    //     qui vaut encore la CLE ANONYME.
    //
    // Les regles de cycle de vie (ouvrir / remplacer le jeton / fermer) sont
    // dans services/realtimeSessionPolicy.js, ou elles sont testees. Ici on
    // ne fait qu'executer la decision.
    let canal = null;

    const fermerCanal = () => {
      if (!canal) return;
      supabase.removeChannel(canal);
      canal = null;
    };

    const ouvrirCanal = async (jeton) => {
      if (annule || canal) return;
      try {
        await supabase.realtime.setAuth(jeton);
      } catch {
        // Le subscribe ci-dessous signalera l'echec avec son statut.
      }
      if (annule || canal) return;
      canal = supabase
        .channel('drafts-pending', { config: { private: true } })
        .on('broadcast', { event: 'drafts_changed' }, () => recompter('broadcast'))
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            // eslint-disable-next-line no-console
            console.log('[drafts-badge] canal prive SUBSCRIBED — badge en temps reel actif');
          } else {
            // `err.message` vient de la bibliotheque, pas de nous : il peut
            // contenir l'URL du websocket, laquelle porte apikey=<jeton>.
            // On le nettoie avant journalisation (consigne Anissa : aucun
            // jeton, meme partiel, dans la console).
            const detail = err ? nettoyerMessageErreur(err.message) : '';
            // eslint-disable-next-line no-console
            console.warn(
              `[drafts-badge] canal prive ${status}${detail ? ` (${detail})` : ''} — `
              + 'le badge se mettra a jour au retour sur l\'onglet, pas en direct',
            );
          }
        });
    };

    const appliquer = (evenement, session) => {
      const { decision, motif } = deciderPourEvenement(evenement, session, Boolean(canal));
      if (decision === DECISION.RIEN) return;
      // Journal SANS jeton : le message est construit par le module.
      // eslint-disable-next-line no-console
      console.log(journalPourDecision(decision, motif));
      if (decision === DECISION.FERMER) { fermerCanal(); return; }
      if (decision === DECISION.REMPLACER_JETON) {
        supabase.realtime.setAuth(session.access_token).catch(() => {});
        return;
      }
      if (decision === DECISION.OUVRIR) ouvrirCanal(session.access_token);
    };

    // Etat au montage : couvre le rechargement de page, ou aucun evenement
    // d'authentification ne sera emis apres coup.
    supabase.auth.getSession()
      .then(({ data }) => {
        if (annule) return;
        if (!data?.session?.access_token) {
          // eslint-disable-next-line no-console
          console.warn(
            '[drafts-badge] aucune session Supabase — canal prive non rejoint. '
            + 'Le badge se mettra a jour au retour sur l\'onglet.',
          );
          return;
        }
        appliquer('INITIAL_SESSION', data.session);
      })
      .catch(() => {});

    // Cycle de vie ensuite : renouvellement de jeton et deconnexion.
    const { data: abonnementAuth } = supabase.auth.onAuthStateChange((evenement, session) => {
      if (annule) return;
      appliquer(evenement, session);
    });

    // Filets de secours, independants du websocket.
    const auRetour = () => {
      if (document.visibilityState === 'visible') recompter('onglet visible');
    };
    const auFocus = () => recompter('focus fenetre');
    const aLaReconnexion = () => recompter('reseau retabli');

    document.addEventListener('visibilitychange', auRetour);
    window.addEventListener('focus', auFocus);
    window.addEventListener('online', aLaReconnexion);

    return () => {
      annule = true;
      document.removeEventListener('visibilitychange', auRetour);
      window.removeEventListener('focus', auFocus);
      window.removeEventListener('online', aLaReconnexion);
      abonnementAuth?.subscription?.unsubscribe();
      fermerCanal();
    };
  }, []);

  // V97.22.2 — Preload phase recommendations DB → cache module au mount.
  // Idempotent (TTL 5 min). Fallback silencieux sur hardcode JS si DB down.
  useEffect(() => {
    preloadPhaseRecommendationsFromSupabase(supabase).then((res) => {
      if (res.ok) {
        // eslint-disable-next-line no-console
        console.log(`[phase-reco] preload OK from ${res.source} (${res.count} phases)`);
      } else {
        // eslint-disable-next-line no-console
        console.warn(`[phase-reco] configuration publiee indisponible (fail-closed): ${res.error}`);
      }
    });
  }, []);

  // V97.11 — Ctrl+K (ou Cmd+K macOS) → focus la barre de recherche
  const searchInputRef = useRef(null);
  useEffect(() => {
    const handleKeydown = (e) => {
      // Ctrl+K (Win/Linux) ou Cmd+K (macOS)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, []);

  // V97.11 — detection plateforme pour afficher le bon raccourci visuel
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutLabel = isMac ? '⌘K' : 'Ctrl+K';

  const allClients = [...sharedClients, ...ownClients];

  // Clients needing follow-up (last consultation > 3 months)
  const clientsToRecontact = allClients.filter(c => {
    const status = getFollowUpStatus(c.id);
    return status === 'recommended' || status === 'urgent';
  });

  let filteredShared = sharedClients;
  let filteredOwn = ownClients;
  if (search.trim()) {
    const q = search.toLowerCase().trim();
    filteredShared = sharedClients.filter(c => (c.prenom || '').toLowerCase().includes(q));
    filteredOwn = ownClients.filter(c => (c.prenom || '').toLowerCase().includes(q));
  }

  // V94.16 : filtre par statut (active = a au moins 1 conso, hors_app = aucune,
  // recontact = clientsToRecontact contient cet id)
  if (statusFilter !== 'all') {
    const recontactIds = new Set(clientsToRecontact.map(c => c.id));
    const passes = (c) => {
      const consCount = getNutritionConsultations(c.id).length;
      if (statusFilter === 'active') return consCount > 0;
      if (statusFilter === 'hors_app') return consCount === 0;
      if (statusFilter === 'recontact') return recontactIds.has(c.id);
      return true;
    };
    filteredShared = filteredShared.filter(passes);
    filteredOwn = filteredOwn.filter(passes);
  }

  // V97.11 — Tri des clientes selon sortBy
  // - 'alert'  : urgent (>6 mois) > recommended (>3 mois) > sans conso > normal,
  //              tie-break par derniere conso desc
  // - 'recent' : derniere conso desc (plus recent en haut)
  // - 'name'   : alphabetique prenom asc
  const sortClients = (clients) => {
    const enriched = clients.map((c) => {
      const consultations = getNutritionConsultations(c.id);
      const lastDate = consultations[0] ? new Date(consultations[0].date).getTime() : 0;
      const followUp = getFollowUpStatus(c.id); // 'urgent' | 'recommended' | null
      let alertScore = 0;
      if (followUp === 'urgent') alertScore = 100;
      else if (followUp === 'recommended') alertScore = 50;
      else if (consultations.length === 0) alertScore = 25; // hors_app
      return { client: c, lastDate, alertScore };
    });
    if (sortBy === 'alert') {
      enriched.sort((a, b) => b.alertScore - a.alertScore || b.lastDate - a.lastDate);
    } else if (sortBy === 'recent') {
      enriched.sort((a, b) => b.lastDate - a.lastDate);
    } else if (sortBy === 'name') {
      enriched.sort((a, b) => (a.client.prenom || '').localeCompare(b.client.prenom || '', 'fr'));
    }
    return enriched.map((e) => e.client);
  };
  filteredShared = sortClients(filteredShared);
  filteredOwn = sortClients(filteredOwn);
  // 2026-07-21 (espace 100 % Anissa) : une seule liste fusionnée, triée
  // globalement. L'origine coaching reste visible sur la carte (« Client de
  // Benoit ») et isOwn pilote toujours les droits (pas de suppression ni
  // d'édition de consultation sur les clientes transférées).
  const sharedIds = new Set(sharedClients.map(c => c.id));
  const filteredAll = sortClients([...filteredOwn, ...filteredShared]);

  // V94.16 : compteurs pour les pills
  const allClientsCount = ownClients.length + sharedClients.length;
  const activeCount = [...ownClients, ...sharedClients].filter(c => getNutritionConsultations(c.id).length > 0).length;
  const horsAppCount = allClientsCount - activeCount;
  const recontactCount = clientsToRecontact.length;

  const totalFiltered = filteredShared.length + filteredOwn.length;

  // Stats
  const now = new Date();
  const consultationsThisMonth = allClients.reduce((sum, c) => {
    return sum + getNutritionConsultations(c.id).filter(n => {
      const d = new Date(n.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, 0);

  return (
    <div className="dashboard anissa-dashboard">
      <style>{`
        @keyframes packDotPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.25); opacity: .85; }
        }
        .pack-dot-active {
          animation: packDotPulse 2.2s ease-in-out infinite;
        }
        .pack-step-tooltip {
          display: block;
        }
        @media (max-width: 768px) {
          .pack-step-tooltip {
            display: none !important;
          }
        }
      `}</style>
      <header className="anissa-dashboard__intro">
        <div>
          <p className="anissa-dashboard__eyebrow">Espace Anissa</p>
          <h1>Vue d’ensemble</h1>
          <p className="anissa-dashboard__subtitle">
            Les priorités, les suivis et les dossiers clients réunis au même endroit.
          </p>
        </div>
        <button className="btn btn-sm btn-anissa-primary anissa-dashboard__new-client" onClick={onNewClient}>
          + Nouveau client
        </button>
      </header>

      <section className="anissa-dashboard__section anissa-today" aria-labelledby="anissa-today-title">
        <div className="anissa-dashboard__section-heading">
          <div>
            <p className="anissa-dashboard__eyebrow">En un coup d’œil</p>
            <h2 id="anissa-today-title">Aujourd’hui</h2>
          </div>
        </div>
        <div className="anissa-today__grid">
          {/* 2026-07-22 — Le point de la semaine (décision Benoit : le récap du
              lundi vit ici, pas par email). Clic → Suivi. */}
          {onOpenSuivi && <WeeklyPulse onOpenSuivi={onOpenSuivi} />}

          {/* Stats */}
          {/* 2026-07-21 (fusion des listes) : une seule tuile « Clientes » — le
              détail own/partagées vit sur les cartes (bandeau Client de Benoit). */}
          <div className="stats-row stats-row-3">
            <div className="stat-card anissa-stat">
              <span className="stat-number">{allClients.length}</span>
              <span className="stat-label">Clientes</span>
            </div>
            <div className="stat-card anissa-stat">
              <span className="stat-number">{consultationsThisMonth}</span>
              <span className="stat-label">Consultations ce mois</span>
            </div>
            <div className={`stat-card anissa-stat${clientsToRecontact.length > 0 ? ' anissa-stat--attention' : ''}`}>
              <span className="stat-number">
                {clientsToRecontact.length}
              </span>
              <span className="stat-label">À recontacter</span>
            </div>
          </div>
        </div>
      </section>

      {/* 23.08.2026 — la derniere connexion existait deja, mais fiche par
          fiche : il fallait ouvrir dix dossiers pour se faire une idee. Ce
          qui compte pour Anissa n'est pas « Clelia s'est connectee a 14h »,
          c'est « trois clientes n'ont pas ouvert leur espace depuis deux
          semaines ». Un signal d'accompagnement, pas de surveillance : on
          n'affiche jamais ce qu'une cliente FAIT dans l'application. */}
      <section className="anissa-dashboard__section anissa-attention" aria-labelledby="anissa-attention-title">
        <div className="anissa-dashboard__section-heading">
          <div>
            <p className="anissa-dashboard__eyebrow">Signaux utiles</p>
            <h2 id="anissa-attention-title">Points d’attention</h2>
          </div>
        </div>
        <div className="anissa-attention__grid">
          <ClientesSilencieuses
            clients={allClients}
            onClickClient={onOpenClient}
            refreshTick={ownClients.length + sharedClients.length}
          />

          {/* Phase C (2026-05-10) : compteur agrege des plans d'analyses par statut */}
          <AnalysisPlansFollowupBlock />

          {/* Clients to re-contact alert */}
          {clientsToRecontact.length > 0 && !search.trim() && (
            <div className="recontact-section attention-card attention-card--wide">
              <h3 className="anissa-section-title recontact-title">
                Clients à recontacter
                <span className="anissa-section-count recontact-count">{clientsToRecontact.length}</span>
              </h3>
              <div className="recontact-list">
                {clientsToRecontact.map(c => {
                  const status = getFollowUpStatus(c.id);
                  const consultations = getNutritionConsultations(c.id);
                  const lastDate = consultations[0]?.date;
                  return (
                    <div key={c.id} className={`recontact-item recontact-${status}`}>
                      <span className="recontact-name">{c.prenom || 'Sans nom'}</span>
                      <span className="recontact-date">Dernière consultation : {formatDate(lastDate)}</span>
                      <span className={`recontact-badge recontact-badge-${status}`}>
                        {status === 'urgent' ? 'Suivi urgent' : 'Suivi recommandé'}
                      </span>
                      <button className="btn btn-xs btn-anissa-primary" onClick={() => onConsultation(c.id)}>
                        + Consultation
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="anissa-directory" aria-labelledby="anissa-directory-title">
      <div className="dashboard-header">
        <div>
          <p className="anissa-dashboard__eyebrow">Dossiers clients</p>
          <h2 id="anissa-directory-title">Mes clients</h2>
          <p className="anissa-directory__summary">{totalFiltered} dossier{totalFiltered !== 1 ? 's' : ''} affiché{totalFiltered !== 1 ? 's' : ''}</p>
        </div>
        {/* 2026-07-19 (revue Benoit) — les outils experts (marquage lu, sync,
            phases, qualité des générations, garde-fous) sont repliés derrière « Outils » :
            le Dashboard redevient la liste des clientes. Brouillons reste
            toujours visible (son badge est actionnable au quotidien). */}
        {showTools && (<>
        {/* V97.11 — batch "Tout marquer lu" : evite a Anissa de cliquer sur
            chaque cliente pour reset le badge "X nouveaux feedbacks". Le
            helper markClientReviewed a son cache 5 min donc safe d'appeler
            sur toutes les clientes (no-op si deja marque recemment). */}
        <button
          onClick={async () => {
            setMarkingAll(true);
            setMarkAllResult(null);
            const allTargets = [...ownClients, ...sharedClients];
            const results = await Promise.allSettled(
              allTargets.map((c) => markClientReviewed(c))
            );
            const marked = results.filter(
              (r) => r.status === 'fulfilled' && r.value?.ok && r.value?.marked
            ).length;
            clearStatusCache();
            setMarkingAll(false);
            setMarkAllResult(marked);
            setTimeout(() => setMarkAllResult(null), 3000);
            if (typeof onRefresh === 'function') onRefresh();
          }}
          disabled={markingAll || (ownClients.length + sharedClients.length) === 0}
          title="Marque toutes les clientes comme vues (réinitialise les badges 'nouveaux feedbacks')"
          style={{
            marginLeft: 'auto', padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.1)', background: 'none',
            color: markingAll ? 'rgba(255,255,255,.3)' : 'rgba(255,255,255,.5)',
            cursor: markingAll ? 'not-allowed' : 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
          }}
        >
          {markingAll
            ? 'Marquage...'
            : markAllResult !== null
              ? `${markAllResult} marquée${markAllResult > 1 ? 's' : ''}`
              : 'Tout marquer lu'}
        </button>
        <button
          onClick={async () => {
            setSyncing(true);
            setSyncResult(null);
            const res = await forceSyncAllConsultations();
            setSyncing(false);
            setSyncResult(res);
            setTimeout(() => setSyncResult(null), 3000);
          }}
          disabled={syncing}
          style={{
            padding:'8px 12px', borderRadius:8,
            border:'1px solid rgba(255,255,255,.1)', background:'none',
            color: syncing ? 'rgba(255,255,255,.3)' : 'rgba(255,255,255,.5)',
            cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize:'.75rem', marginRight:8, minHeight:36,
          }}
        >
          {syncing ? 'Sync...' : syncResult ? `${syncResult.synced} synced` : 'Sync cloud'}
        </button>
        {/* Santé de la synchro : file d'attente en échec (ambre) ou dernier
            succès. Un échec silencieux ne doit plus être invisible. */}
        {(() => {
          const h = getSyncHealth();
          // V97.153 — la quarantaine passe AVANT « en attente » et en rouge :
          // « en attente » se resorbe tout seul, « bloque » non. Ces elements
          // ne repartiront jamais sans une decision, ils doivent se voir.
          if (h.quarantine > 0) {
            return (
              <span
                className="anissa-directory__sync-status anissa-directory__sync-status--blocked"
                title={`${h.quarantineResume}\n\nCes elements ne seront plus rejoues automatiquement. Leurs donnees sont conservees et restent exportables et rejouables une fois la cause corrigee.`}
                style={{ fontSize: '.72rem', color: '#f5a3a3', marginRight: 8, whiteSpace: 'nowrap', cursor: 'help' }}
              >
                ⚠ {h.quarantine} bloqué{h.quarantine > 1 ? 's' : ''} en synchro
              </span>
            );
          }
          if (h.pending > 0) {
            return (
              <span className="anissa-directory__sync-status anissa-directory__sync-status--pending" style={{ fontSize: '.72rem', color: '#e9b876', marginRight: 8, whiteSpace: 'nowrap' }}>
                {h.pending} en attente
              </span>
            );
          }
          if (!h.lastSyncAt) return null;
          const mins = Math.floor((Date.now() - new Date(h.lastSyncAt).getTime()) / 60000);
          const label = mins < 1 ? "à l'instant" : mins < 60 ? `il y a ${mins} min` : mins < 1440 ? `il y a ${Math.floor(mins / 60)} h` : `il y a ${Math.floor(mins / 1440)} j`;
          return (
            <span className="anissa-directory__sync-status anissa-directory__sync-status--ok" style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.35)', marginRight: 8, whiteSpace: 'nowrap' }}>
              ✓ {label}
            </span>
          );
        })()}
        </>)}
        {/* V97.23.1 — bouton cockpit brouillons IA en attente */}
        <button
          onClick={() => setShowDraftsPanel(true)}
          title="Brouillons préparés aux transitions de phase, en attente de validation"
          style={{
            marginLeft: showTools ? undefined : 'auto',
            padding: '8px 12px', borderRadius: 8,
            border: `1px solid ${pendingDraftsCount > 0 ? 'rgba(126,94,199,.5)' : 'rgba(255,255,255,.1)'}`,
            background: pendingDraftsCount > 0 ? 'rgba(126,94,199,.18)' : 'none',
            color: pendingDraftsCount > 0 ? '#c4b3f0' : 'rgba(255,255,255,.5)',
            cursor: 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
            fontWeight: pendingDraftsCount > 0 ? 600 : 400,
          }}
        >
          Brouillons
          {pendingDraftsCount > 0 && (
            <span className="anissa-directory__draft-count" style={{
              marginLeft: 6, padding: '1px 7px', borderRadius: 999,
              background: '#7e5ec7', color: 'white', fontSize: 10, fontWeight: 700,
            }}>{pendingDraftsCount}</span>
          )}
        </button>
        {showTools && (<>
        {/* V97.22 — bouton ouverture cockpit recommandations par phase */}
        <button
          onClick={() => setShowPhasesPanel(true)}
          title="Éditer les recommandations cliniques par phase du parcours (aliments, cuissons, compléments)"
          style={{
            padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.1)', background: 'none',
            color: 'rgba(255,255,255,.5)', cursor: 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
          }}
        >
          Phases
        </button>
        {/* V97.21 (OBS-2) — bouton stats observability */}
        <button
          onClick={() => setShowObservabilityPanel(true)}
          title="Qualité des générations de plans (couverture clinique et qualité rédactionnelle)"
          style={{
            padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.1)', background: 'none',
            color: 'rgba(255,255,255,.5)', cursor: 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
          }}
        >
          Qualité des générations
        </button>
        {/* V97.19 — bouton ouverture cockpit garde-fous cliniques */}
        <button
          onClick={() => setShowGuardrailsPanel(true)}
          title="Éditer la matrice des garde-fous cliniques (phrases interdites, micronutriments, évictions par profil)"
          style={{
            padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.1)', background: 'none',
            color: 'rgba(255,255,255,.5)', cursor: 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
          }}
        >
          Garde-fous
        </button>
        </>)}
        <button
          onClick={() => setShowTools((v) => !v)}
          title="Outils avancés : marquage lu, synchronisation, phases, qualité des générations, garde-fous"
          style={{
            padding: '8px 12px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,.1)',
            background: showTools ? 'rgba(255,255,255,.06)' : 'none',
            color: 'rgba(255,255,255,.5)', cursor: 'pointer',
            fontSize: '.75rem', marginRight: 8, minHeight: 36,
          }}
        >
          {showTools ? 'Outils ▾' : 'Outils ▸'}
        </button>
      </div>

      <div className="anissa-directory__toolbar">
      <div className="search-bar anissa-directory__search" style={{ position: 'relative' }}>
        <input
          ref={searchInputRef}
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par prénom..."
          className="search-input"
          style={{ paddingRight: 56 }}
        />
        {/* V97.11 — indicateur raccourci clavier (decouvrabilite) */}
        <kbd
          className="anissa-directory__shortcut"
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '.7rem',
            fontFamily: 'inherit',
            fontWeight: 600,
            padding: '2px 7px',
            borderRadius: 5,
            border: '1px solid rgba(255,255,255,.12)',
            background: 'rgba(255,255,255,.04)',
            color: 'rgba(255,255,255,.45)',
            pointerEvents: 'none',
            letterSpacing: '.02em',
          }}
        >
          {shortcutLabel}
        </kbd>
      </div>

      {/* V94.16 : pills de filtre rapide par statut + V97.11 : tri */}
      <div className="anissa-dash-toolbar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, marginTop: -4 }}>
        {[
          { id: 'all', label: 'Tous', count: allClientsCount, color: 'rgba(255,255,255,.18)' },
          { id: 'active', label: 'Active', count: activeCount, color: 'rgba(106,191,138,.4)' },
          { id: 'hors_app', label: 'Hors app', count: horsAppCount, color: 'rgba(255,255,255,.25)' },
          { id: 'recontact', label: 'À recontacter', count: recontactCount, color: 'rgba(232,160,64,.5)' },
        ].map(p => {
          const active = statusFilter === p.id;
          return (
            <button
              key={p.id}
              type="button"
              className={`anissa-filter${active ? ' anissa-filter--active' : ''}`}
              onClick={() => setStatusFilter(p.id)}
              title={`Filtrer : ${p.label}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 999,
                border: `1px solid ${active ? p.color : 'rgba(255,255,255,.1)'}`,
                background: active ? `${p.color.replace('.4)', '.1)').replace('.5)', '.12)').replace('.25)', '.06)').replace('.18)', '.05)')}` : 'transparent',
                color: active ? 'var(--text)' : 'rgba(255,255,255,.55)',
                fontSize: '.78rem', fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                transition: 'all .15s',
              }}
            >
              <span className="anissa-filter__label">{p.label}</span>
              <span className="anissa-filter__count" style={{
                fontSize: '.7rem',
                background: 'rgba(0,0,0,.25)',
                padding: '1px 8px', borderRadius: 999,
                color: 'rgba(255,255,255,.6)',
              }}>{p.count}</span>
            </button>
          );
        })}

        {/* V97.11 — Dropdown tri (push a droite) */}
        <div className="anissa-directory__sort" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="anissa-directory__sort-label" style={{ fontSize: '.72rem', color: 'rgba(255,255,255,.4)' }}>Tri</span>
          <select
            className="anissa-directory__sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,.1)',
              background: 'rgba(255,255,255,.03)',
              color: 'rgba(255,255,255,.7)',
              fontSize: '.78rem',
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <option value="alert">Alertes en premier</option>
            <option value="recent">Consultation récente</option>
            <option value="name">Alphabétique</option>
          </select>
        </div>
      </div>
      </div>

      {allClients.length === 0 ? (
        <div className="dashboard-empty">
          <div className="empty-title">Aucun client</div>
          <p>Crée ta première cliente ou attends que {PARTNER_IDENTITY.name} ajoute une cliente en Suivi Complet / Intensif.</p>
          <button className="btn btn-anissa-primary" onClick={onNewClient} style={{ marginTop: 16, padding: '12px 28px' }}>
            + Nouveau client
          </button>
        </div>
      ) : (
        <>
          {/* 2026-07-21 (espace 100 % Anissa) : liste unique fusionnée — la
              séparation own/partagées en deux sections datait de V87.6, elle
              n'a plus de sens sans l'interface Benoit. La carte garde le
              bandeau « Client de Benoit » et les droits restreints. */}
          {filteredAll.length > 0 && (
            <div className="anissa-section">
              {/* Pas de titre de section : « Mes clients » (dashboard-header)
                  coiffe déjà la liste unique, un 2e titre faisait doublon. */}
              <div className="anissa-client-list">
                {filteredAll.map((client, i) => (
                  <ClientCard key={client.id} client={client} i={i} onConsultation={onConsultation} onEditConsultation={sharedIds.has(client.id) ? undefined : onEditConsultation} onViewHistory={onViewHistory} onOpen={onOpenClient} isOwn={!sharedIds.has(client.id)} onRefresh={onRefresh} onSendPackReview={onSendPackReview} />
                ))}
              </div>
            </div>
          )}

          {filteredAll.length === 0 && (
            <div className="dashboard-empty">
              <p>Aucun client ne correspond a la recherche.</p>
            </div>
          )}
        </>
      )}

      </section>

      {/* V97.19 — Cockpit garde-fous cliniques (modal) */}
      {showGuardrailsPanel && (
        <ClinicalGuardrailsPanel onClose={() => setShowGuardrailsPanel(false)} />
      )}

      {/* V97.21 (OBS-2) — Dashboard stats observability (modal) */}
      {showObservabilityPanel && (
        <ObservabilityPanel onClose={() => setShowObservabilityPanel(false)} />
      )}

      {/* V97.22 — Cockpit recommandations par phase (modal) */}
      {showPhasesPanel && (
        <PhaseRecommendationsPanel onClose={() => setShowPhasesPanel(false)} />
      )}

      {/* V97.23.1 — Cockpit brouillons IA en attente de validation */}
      {showDraftsPanel && (
        <PendingDraftsPanel
          onClose={() => setShowDraftsPanel(false)}
          clientsById={Object.fromEntries(allClients.map((c) => [c.id, c]))}
        />
      )}
    </div>
  );
}
